/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { defaultTheme, Provider as ProviderV3 } from '@adobe/react-spectrum';
import Provider from '@react/react-spectrum/Provider';
import { NotFoundPage } from './components/NotFoundPage/Loadable';
import { useTranslation } from 'react-i18next';
import './index.scss';
import { ThemeContext } from 'context/themeContext';
import { ThemeContextType } from 'types/theme';
import { CheckLogin } from './pages/CheckLogin/Loadable';
import { Layout } from './pages/Layout/Loadable';
import { GroupManagement } from './pages/GroupManagement/Loadable';
import { PersonalAccessTokens } from './pages/PersonalAccessTokens/Loadable';
import { Roles } from './pages/Roles/Loadable';
import { Users } from './pages/Users/Loadable';
import { ToastContainer } from '@adobe/react-spectrum';
import ProtectedRoute from './ProtectedRoute';
import {
  selectIsAuthenticated,
  selectUserProfile
} from './pages/Layout/slice/selectors';
import { useSelector } from 'react-redux';
import { UnAuthorized } from './pages/UnAuthorized/Loadable';
import { ServerError } from './pages/ServerError/Loadable';
import { Home } from './pages/Home/Loadable';
import { UserGuide } from './pages/UserGuide/Loadable';
import { TestDecisionPopup } from './pages/TestDecisionPopup/Loadable';
import { TestDecisionScreen } from './pages/TestDecisionScreen/Loadable';
import { TestEditDecisionPopup } from './pages/TestEditDecisionPopup/Loadable';
import { TestAdditionalInfoPopup } from './pages/TestAdditionalInfoPopup/Loadable';
import { TestDACIContactPopup } from './pages/TestDACIContactPopup/Loadable';
import { useLayoutSlice } from './pages/Layout/slice';
import { useGlobalCacheSlice } from './store/globalCacheSlice';
import { usePendingApprovalsSlice } from './store/pendingApprovalsSlice';
import { useHomeSlice } from './pages/Home/slice';
import { useDispatch } from 'react-redux';
import { MilestonesGridReport } from './pages/MilestonesGrid/MilestonesGridReport';
import { ReviewApprovalDashboard } from './pages/ReviewApproval/Loadable';
import { MobileFeedbackPage } from './pages/ReviewApproval/mobile/FeedbackLoadable';
import { MobileHomeRedirect } from './pages/ReviewApproval/mobile/MobileHomeRedirect';
import { AIAssistantDock } from './components/AIAssistantDock';

export function App() {
  const { i18n } = useTranslation();
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;
  const dispatch = useDispatch();

  // Initialize the layout slice before using any of its selectors
  useLayoutSlice();

  // Initialize the global cache slice and load data at app startup
  const { actions: globalCacheActions } = useGlobalCacheSlice();

  // Initialize the home slice for hierarchy filters
  const { actions: homeActions } = useHomeSlice();

  // Initialize the pending-approvals slice here (not inside ReviewApproval's
  // own page-scoped slice) so the Home icon's notification indicator
  // (LeftSidebar.tsx) can read this state on first paint, on every route -
  // not only after /review-approval has been visited once. See
  // pendingApprovalsSlice.ts's own comment for the full reasoning.
  const { actions: pendingApprovalsActions } = usePendingApprovalsSlice();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Load authenticated startup data only after profile/auth bootstrap completes
  React.useEffect(() => {
    if (isAuthenticated) {
      dispatch(globalCacheActions.loadGlobalCacheData());
    }
  }, [dispatch, globalCacheActions, isAuthenticated]);

  // Load the pending-approvals count (drives the Home icon's notification
  // dot) once, right after the same profile/auth bootstrap the other
  // startup calls wait for - the backend response is the sole source of
  // truth for the indicator, so this is a fetch, not a locally-computed flag.
  React.useEffect(() => {
    if (isAuthenticated) {
      dispatch(pendingApprovalsActions.loadPendingApprovalsCount());
    }
  }, [dispatch, pendingApprovalsActions, isAuthenticated]);

  // Load product hierarchy filters only when authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      dispatch(homeActions.loadHierarchyFilters({}));
    }
  }, [dispatch, homeActions, isAuthenticated]);

  // Load Must Win filter options at app startup
  React.useEffect(() => {
    dispatch(homeActions.loadMustWinFilterOptions());
  }, [dispatch, homeActions]);

  const userProfile = useSelector(selectUserProfile);

  const checkAdmins = (): boolean => {
    // Use the isAdmin property directly from user profile
    // This matches the RBAC system's admin check
    return userProfile?.isAdmin || false;
  };

  const selecctedThem =
    theme === 'dark' || theme === 'darkest' ? 'dark' : 'light';

  return (
    <>
      <Provider theme={selecctedThem}>
        <ProviderV3 theme={defaultTheme} colorScheme={selecctedThem}>
          <BrowserRouter>
            <Helmet
              titleTemplate="Roadmap"
              defaultTitle="Roadmap"
              htmlAttributes={{ lang: i18n.language }}
            >
              <meta name="description" content="Roadmap" />
            </Helmet>
            <div
              className="spectrum-ShellContainer"
              data-omega-feature="shell header"
              data-omega-widget="shell header buttons"
              data-omega-widget-type="button"
            >
              <Provider theme={selecctedThem}>
                <ToastContainer />
                {/* Mobile-width viewports sitting on another nav destination
                    (Roadmap/Milestones/...) redirect once to the Review &
                    Approval mobile dashboard - the only page with a mobile
                    layout. Already being on /review-approval* is left
                    untouched. See MobileHomeRedirect.tsx. */}
                <MobileHomeRedirect />
                <Routes>
                  <Route path="/checkLogin" element={<CheckLogin />} />
                  <Route element={<Layout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/roadmap" element={<Home />} />
                    <Route path="/user-guide" element={<UserGuide />} />
                    <Route
                      path="/milestones"
                      element={<MilestonesGridReport />}
                    />
                    <Route
                      path="/milestones/moments/:issueId"
                      element={<MilestonesGridReport />}
                    />
                    <Route
                      path="/milestones/moments/:issueId/comments/:commentId"
                      element={<MilestonesGridReport />}
                    />
                    <Route
                      path="/review-approval"
                      element={<ReviewApprovalDashboard />}
                    />
                    <Route
                      path="/review-approval/moments/:issueId"
                      element={<ReviewApprovalDashboard />}
                    />
                    <Route
                      path="/review-approval/moments/:issueId/comments/:commentId"
                      element={<ReviewApprovalDashboard />}
                    />
                    {/* Mobile-only Feedback screen (see mobile/MobileFeedbackPage.tsx)
                        - its own route rather than a state toggle over the
                        mobile dashboard, per the mobile-approach decision. */}
                    <Route
                      path="/review-approval/feedback/:requestId"
                      element={<MobileFeedbackPage />}
                    />
                    <Route path="/moments/:issueId" element={<Home />} />
                    <Route
                      path="/moments/:issueId/comments/:commentId"
                      element={<Home />}
                    />
                    <Route
                      path="/test-decision-popup"
                      element={<TestDecisionPopup />}
                    />
                    <Route
                      path="/test-decision-screen"
                      element={<TestDecisionScreen />}
                    />
                    <Route
                      path="/test-edit-decision"
                      element={<TestEditDecisionPopup />}
                    />
                    <Route
                      path="/test-additional-info"
                      element={<TestAdditionalInfoPopup />}
                    />
                    <Route
                      path="/test-daci-contact"
                      element={<TestDACIContactPopup />}
                    />
                    <Route
                      path="/settings/group-management"
                      element={
                        <ProtectedRoute isAdmin={checkAdmins()}>
                          <GroupManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings/user-roles"
                      element={
                        <ProtectedRoute isAdmin={checkAdmins()}>
                          <Roles />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings/account"
                      element={
                        <ProtectedRoute isAdmin={checkAdmins()}>
                          <Users />
                        </ProtectedRoute>
                      }
                    />
                    {/* Per-user, NOT admin-only — deliberately no ProtectedRoute. */}
                    <Route
                      path="/settings/tokens"
                      element={<PersonalAccessTokens />}
                    />
                    <Route path="/403" element={<UnAuthorized />} />
                    <Route path="/server-error" element={<ServerError />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Routes>
                <AIAssistantDock />
              </Provider>
            </div>
          </BrowserRouter>
        </ProviderV3>
      </Provider>
    </>
  );
}
App.displayName = 'InitialApp';
